// SchedulePlanModal — the Production Calendar's "Schedule Plan" modal (Script
// List → Ready tab), extracted 2026-09-15 (D-088) so the Production page's
// Shoot stage can open the SAME modal for a card. Two entry points:
//   • Ready tab:   <SchedulePlanModal scriptId=… />           → POST /api/tal/:scriptId/confirm
//   • Production:  <SchedulePlanModal card={{ id, title, clientName, scriptId, segments }} />
//                                                              → PUT /api/shoot-segments/:id/plan
// In card mode the rows are seeded from the card's UNFILMED segments when it
// has any (edit), otherwise from the script's suggestion (first plan); filmed
// segments are listed read-only and left alone by the server. Cards without a
// script (Riff) or without characters plan talent-only rows.
import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../lib/api';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildCalDays(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  const days = [];
  const cur  = new Date(start);
  while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

// ── Per-talent mini availability calendar ────────────────────────────────────
// Renders a compact month grid highlighting the dates one specific talent is
// available. Click a highlighted day to pick it — used inline in the Schedule
// Plan modal when an unavailable talent is selected, so the Producer can
// pivot to a date that talent actually works.
export function TalentAvailabilityMini({ talentId, talentName, baseDate, onPick }) {
  const init = baseDate ? new Date(baseDate + 'T00:00:00') : new Date();
  const [year,       setYear]       = useState(init.getFullYear());
  const [month,      setMonth]      = useState(init.getMonth());
  const [availDates, setAvailDates] = useState(new Set());
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!talentId) return;
    const mm    = String(month + 1).padStart(2, '0');
    const first = `${year}-${mm}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const last    = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
    let cancelled = false;
    setLoading(true);
    api.get(`/api/talent-availability/by-range?from=${first}&to=${last}`)
      .then(r => {
        if (cancelled) return;
        const dates = new Set();
        for (const [d, rows] of Object.entries(r.data || {})) {
          if (rows.some(rr => rr.talentId === talentId)) dates.add(d);
        }
        setAvailDates(dates);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [talentId, year, month]);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  function buildDays(y, m) {
    const first = new Date(y, m, 1);
    const last  = new Date(y, m + 1, 0);
    const start = new Date(first); start.setDate(first.getDate() - first.getDay());
    const end   = new Date(last);  end.setDate(last.getDate() + (6 - last.getDay()));
    const out = [], cur = new Date(start);
    while (cur <= end) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  }
  function dKey(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
  function prevMonth() { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); }
  function nextMonth() { if (month === 11) { setMonth(0);  setYear(y => y + 1); } else setMonth(m => m + 1); }

  const days = buildDays(year, month);
  return (
    <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <button type="button" onClick={prevMonth} style={{ background: 'none', border: 'none', color: 'var(--tx-secondary)', cursor: 'pointer', fontSize: 13, padding: '0 6px', fontFamily: 'var(--font)' }}>‹</button>
        <div style={{ fontSize: 10, color: 'var(--tx-muted)' }}>
          {talentName} available — {MONTHS[month]} {year}{loading ? ' · loading…' : ''}
        </div>
        <button type="button" onClick={nextMonth} style={{ background: 'none', border: 'none', color: 'var(--tx-secondary)', cursor: 'pointer', fontSize: 13, padding: '0 6px', fontFamily: 'var(--font)' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {['S','M','T','W','T','F','S'].map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 9, color: 'var(--tx-faint)', fontWeight: 600, padding: '2px 0' }}>{d}</div>
        ))}
        {days.map(d => {
          const k     = dKey(d);
          const other = d.getMonth() !== month;
          const avail = availDates.has(k) && !other;
          return (
            <button
              key={k}
              type="button"
              disabled={!avail}
              onClick={() => avail && onPick(k)}
              style={{
                padding: '4px 0', fontSize: 11, borderRadius: 3, border: 'none',
                cursor: avail ? 'pointer' : 'default',
                fontFamily: 'var(--font)',
                background: avail ? 'rgba(74,222,128,0.15)' : 'transparent',
                color: avail ? '#4ade80' : other ? 'var(--tx-faint)' : 'var(--tx-secondary)',
                fontWeight: avail ? 600 : 400,
              }}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      {!loading && availDates.size === 0 && (
        <div style={{ marginTop: 6, fontSize: 10, color: 'var(--tx-muted)', textAlign: 'center' }}>
          No availability this month.
        </div>
      )}
    </div>
  );
}

// ── Large calendar picker (Schedule Plan: double-click a shoot-day field) ────
// A full-size month calendar in its own modal so a shoot day can be picked
// visually. Day cells carry the count of available talent for orientation.
export function BigCalendarPickerModal({ value, title, onPick, onClose }) {
  const init = value ? new Date(value + 'T00:00:00') : new Date();
  const [year,   setYear]   = useState(init.getFullYear());
  const [month,  setMonth]  = useState(init.getMonth());
  const [counts, setCounts] = useState({});
  const days     = useMemo(() => buildCalDays(year, month), [year, month]);
  const todayKey = dateKey(new Date());

  useEffect(() => {
    const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const last = new Date(year, month + 1, 0);
    let cancelled = false;
    api.get(`/api/talent-availability/date-counts?from=${from}&to=${dateKey(last)}`)
      .then(r => { if (!cancelled) setCounts(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [year, month]);

  function prevMonth() { if (month === 0)  { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); }
  function nextMonth() { if (month === 11) { setMonth(0);  setYear(y => y + 1); } else setMonth(m => m + 1); }

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10001 }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', width: '80vw', maxWidth: 760, maxHeight: '88vh', overflowY: 'auto', padding: '18px 22px 22px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-primary)' }}>{title || 'Pick a shoot day'}</div>
          <button className="vc-close" onClick={onClose}>×</button>
        </div>
        <div className="cal-month-nav">
          <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
          <div className="cal-month-label">{MONTH_NAMES[month]} {year}</div>
          <button className="cal-nav-btn" onClick={nextMonth}>›</button>
        </div>
        <div className="cal-grid">
          {DAY_NAMES.map(d => <div key={d} className="cal-dow">{d}</div>)}
          {days.map(day => {
            const key     = dateKey(day);
            const otherMo = day.getMonth() !== month;
            const isSel   = key === value;
            const isToday = key === todayKey;
            const count   = otherMo ? 0 : (counts[key] || 0);
            return (
              <div
                key={key}
                className={`cal-day ${isToday ? 'today' : ''} ${otherMo ? 'other-month' : ''}`}
                style={{
                  cursor: otherMo ? 'default' : 'pointer', minHeight: 64,
                  background: isSel ? 'rgba(139,109,255,0.18)' : undefined,
                  outline: isSel ? '2px solid var(--violet)' : undefined, outlineOffset: '-1px',
                }}
                onClick={() => { if (!otherMo) { onPick(key); onClose(); } }}
              >
                <div className="cal-day-num" style={{ color: isSel ? 'var(--violet)' : undefined }}>{day.getDate()}</div>
                {count > 0 && (
                  <div title={`${count} talent available this day`} style={{ fontSize: 9, color: '#4ade80', padding: '0 4px' }}>
                    {count} avail
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Schedule Plan modal — opens from TAL "Schedule →" ────────────────────────
// Loads /api/tal/:scriptId/suggest and seeds one CAM segment per suggested cam
// group, then lets the Producer reshape the plan freely: characters are picked
// per segment from a dropdown (a CAM segment may not include every character),
// picking one reveals its talent dropdown, "+ Add CAM segment" appends another
// segment with the same fields, and double-clicking a shoot-day field opens a
// full calendar picker. POSTs to /api/tal/:scriptId/confirm to create segments.
export default function SchedulePlanModal({ scriptId: scriptIdProp, initialDay, onClose, onConfirmed, card = null }) {
  // Card mode (Production page → Shoot stage): plan/edit the segments of an
  // existing production. Ready-tab mode is byte-for-byte the pre-extraction flow.
  const cardMode     = Boolean(card);
  const scriptId     = cardMode ? (card.scriptId || null) : scriptIdProp;
  const filmedSegs   = cardMode ? (card.segments || []).filter(sg => sg.shootStatus === 'filmed') : [];
  const editableSegs = cardMode ? (card.segments || []).filter(sg => sg.shootStatus !== 'filmed') : [];
  const isEdit       = cardMode && editableSegs.length > 0;
  const [plan,       setPlan]       = useState(null); // suggest payload
  const [loading,    setLoading]    = useState(true);
  // One entry per CAM segment box:
  // { key, camNumber, shootDay, segmentLabel, setting, noCoverage, available,
  //   talents: [{ rowKey, characterId, characterName, gender, talentId, talentName }] }
  const [segments,   setSegments]   = useState([]);
  const [allTalent,  setAllTalent]  = useState([]);   // [{ id, name, talentGender }]
  const [confirming, setConfirming] = useState(false);
  const [err,        setErr]        = useState('');
  const [calPicker,  setCalPicker]  = useState(null); // key of the segment picking a day
  // D-177 (no-guess casting): characters with no cast and no client default come back from the suggestion with an EMPTY slot;
  // a save with any of them unresolved opens the "Casting incomplete" dialog — pick a performer, or "No performer — leave uncast".
  const [uncastIds,  setUncastIds]  = useState(() => new Set());   // characterIds the producer chose to leave uncast
  const [castDialog, setCastDialog] = useState(null);              // null | [{ characterId, characterName }] — the slots named when it opened
  const [serverNeeds, setServerNeeds] = useState([]);              // a refusal from the API (old tab / stale payload) adds its names here
  const seq = useRef(0);
  const nextKey = () => `k${++seq.current}`;

  // Every live character on the script (union across suggested cam groups) —
  // the per-segment character dropdowns pick from this set.
  const allCharacters = useMemo(() => {
    const map = new Map();
    for (const g of (plan?.camGroups || [])) {
      for (const c of g.characters) if (!map.has(c.id)) map.set(c.id, c);
    }
    return [...map.values()];
  }, [plan]);

  // Suggested casting per character (from the suggest payload) — prefills the
  // talent dropdown when a character is picked on a fresh row.
  const suggestedByCharacter = useMemo(() => {
    const map = {};
    for (const g of (plan?.camGroups || [])) {
      for (const s of (g.suggestion || [])) {
        if (s.characterId && s.talentId) map[s.characterId] = { talentId: s.talentId, talentName: s.talentName };
      }
    }
    return map;
  }, [plan]);

  // Load every active talent up front so the dropdowns can show all
  // gender-matching options (not just those available on the chosen day).
  useEffect(() => {
    api.get('/api/talent').then(r => setAllTalent(r.data)).catch(() => {});
  }, []);

  // Load the per-script suggestion + seed the segment list. After seeding,
  // fetch availability for each segment's bestDay so the dropdowns can mark
  // which talent are available without the user having to change the date.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Card mode without a script (Riff) has no suggestion to fetch — plan from
    // an empty character set (talent-only rows).
    const fetchPlan = scriptId
      ? api.get(`/api/tal/${scriptId}/suggest`).then(r => r.data)
      : Promise.resolve({ scriptId: null, scriptName: card?.title || '(Untitled production)', client: card?.clientName ? { name: card.clientName } : null, camGroups: [] });
    fetchPlan
      .then(async data => {
        if (cancelled) return;
        const planData = cardMode
          ? { ...data, scriptName: card.title || data.scriptName, client: data.client || (card.clientName ? { name: card.clientName } : null) }
          : data;
        setPlan(planData);
        const emptyRow = () => ({ rowKey: nextKey(), characterId: null, characterName: null, gender: 'either', talentId: null, talentName: null });
        let seed;
        if (isEdit) {
          // EDIT: rows come from the card's unfilmed segments. A segment talent
          // row carries no character, so map talent → character through the
          // script's resolved casting (suggestion entries flagged resolved).
          const charByTalent = new Map();
          for (const g of (planData.camGroups || [])) {
            for (const sg of (g.suggestion || [])) {
              if (sg.resolved && sg.talentId && !charByTalent.has(sg.talentId)) charByTalent.set(sg.talentId, sg);
            }
          }
          seed = editableSegs.map((sg, i) => {
            const talents = (sg.talents || []).map(t => {
              const c = t.talentId ? charByTalent.get(t.talentId) : null;
              return {
                rowKey: nextKey(),
                characterId: c?.characterId || null, characterName: c?.characterName || null, gender: c?.gender || 'either',
                talentId: t.talentId || null, talentName: t.talent?.name || t.talentDisplayName || null,
              };
            });
            return {
              key: nextKey(), camNumber: i + 1, existingId: sg.id,
              shootDay: sg.shootDay ? String(sg.shootDay).slice(0, 10) : '',
              segmentLabel: sg.segmentLabel || `Cam ${i + 1}`, setting: sg.setting || '',
              noCoverage: false, available: null,
              talents: talents.length ? talents : [emptyRow()],
            };
          });
        } else if ((planData.camGroups || []).length) {
          // initialDay pins every segment to a pre-picked day; otherwise the day
          // stays EMPTY — 2026-09-02 overhaul: assigning segments is a separate
          // act from scheduling, which happens on the Talent Calendar once
          // talent is booked. (bestDay no longer pre-fills.)
          seed = planData.camGroups.map(g => ({
            key:          nextKey(),
            camNumber:    g.camAssignment,
            shootDay:     initialDay || '',
            segmentLabel: `Cam ${g.camAssignment}`,
            setting:      '',
            noCoverage:   Boolean(g.noCoverage),
            available:    null,
            talents:      (g.suggestion || []).map(sg => ({
              rowKey: nextKey(),
              characterId: sg.characterId, characterName: sg.characterName, gender: sg.gender,
              talentId:    sg.talentId, talentName: sg.talentName,
            })),
          }));
        } else if (cardMode) {
          // First plan on a card whose script has no characters (or no script):
          // one CAM segment with one talent-only row to fill.
          seed = [{ key: nextKey(), camNumber: 1, shootDay: initialDay || '', segmentLabel: 'Cam 1', setting: '', noCoverage: false, available: null, talents: [emptyRow()] }];
        } else {
          seed = [];
        }
        setSegments(seed);

        // Fetch availability for each unique day in parallel so the
        // dropdowns and override warnings have data on first render.
        const uniqueDays = [...new Set(seed.map(sg => sg.shootDay).filter(Boolean))];
        const availByDay = {};
        await Promise.all(uniqueDays.map(async d => {
          try {
            const av = await api.get(`/api/talent-availability/by-date?date=${d}`);
            availByDay[d] = av.data;
          } catch { availByDay[d] = []; }
        }));
        if (cancelled) return;
        setSegments(prev => prev.map(sg => (sg.shootDay && availByDay[sg.shootDay]
          ? { ...sg, available: availByDay[sg.shootDay] }
          : sg)));
      })
      .catch(e => setErr(e.response?.data?.error || 'Failed to load plan.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scriptId, initialDay]); // eslint-disable-line react-hooks/exhaustive-deps

  function patchSegment(segKey, patch) {
    setSegments(prev => prev.map(s => (s.key === segKey ? { ...s, ...patch } : s)));
  }
  function patchRow(segKey, rowKey, patch) {
    setSegments(prev => prev.map(s => (s.key !== segKey ? s : {
      ...s, talents: s.talents.map(t => (t.rowKey === rowKey ? { ...t, ...patch } : t)),
    })));
  }

  // When a segment's shootDay changes, re-query availability for that date so
  // its talent dropdowns and override warnings reflect the new day.
  async function handleDateChange(segKey, newDate) {
    patchSegment(segKey, { shootDay: newDate, available: null });
    if (!newDate) return;
    try {
      const r = await api.get(`/api/talent-availability/by-date?date=${newDate}`);
      setSegments(prev => prev.map(s => (s.key === segKey ? { ...s, available: r.data } : s)));
    } catch { /* silent */ }
  }

  function genderMatchesTalent(charGender, talGender) {
    // Untagged characters count as 'either' (D-054) — never block on tags.
    const cg = charGender || 'either';
    if (cg === 'either' || talGender === 'either') return true;
    return cg === talGender;
  }

  // For a segment + character gender, list every gender-matching talent — not
  // just those available on the chosen day. Each option carries an
  // `isAvailable` flag so the dropdown can mark unavailable picks (and the
  // override warning still fires for them). Available talent sort first.
  function matchOptions(seg, charGender) {
    const availIds = new Set((seg?.available || []).map(a => a.talentId));
    const matches  = allTalent
      .filter(t => genderMatchesTalent(charGender, t.talentGender))
      .map(t => ({
        talentId:     t.id,
        talentName:   t.name,
        talentGender: t.talentGender,
        isAvailable:  availIds.has(t.id),
      }));
    matches.sort((a, b) => {
      if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
      return (a.talentName || '').localeCompare(b.talentName || '');
    });
    return matches;
  }

  // Picking a character on a row resets that row's talent to the character's
  // suggested casting (when there is one).
  function setRowCharacter(segKey, rowKey, characterId) {
    const c = allCharacters.find(x => x.id === characterId) || null;
    const suggested = c ? suggestedByCharacter[c.id] : null;
    patchRow(segKey, rowKey, {
      characterId:   c?.id || null,
      characterName: c?.characterName || null,
      gender:        c?.gender || 'either',
      talentId:      suggested?.talentId || null,
      talentName:    suggested?.talentName || null,
    });
  }
  function addCharacterRow(segKey) {
    setSegments(prev => prev.map(s => (s.key !== segKey ? s : {
      ...s,
      talents: [...s.talents, { rowKey: nextKey(), characterId: null, characterName: null, gender: 'either', talentId: null, talentName: null }],
    })));
  }
  function removeCharacterRow(segKey, rowKey) {
    setSegments(prev => prev.map(s => (s.key !== segKey ? s : {
      ...s, talents: s.talents.filter(t => t.rowKey !== rowKey),
    })));
  }

  // The little + under the CAM segment boxes: appends another segment carrying
  // the same field set (CAM number, shoot day, segment label, setting,
  // characters), numbered after the highest existing CAM.
  function addSegment() {
    setSegments(prev => {
      const nextCam = prev.length ? Math.max(...prev.map(s => s.camNumber)) + 1 : 1;
      return [...prev, {
        key: nextKey(), camNumber: nextCam, shootDay: '', segmentLabel: `Cam ${nextCam}`,
        setting: '', noCoverage: false, available: null,
        talents: [{ rowKey: nextKey(), characterId: null, characterName: null, gender: 'either', talentId: null, talentName: null }],
      }];
    });
  }
  function removeSegment(segKey) {
    setSegments(prev => (prev.length > 1 ? prev.filter(s => s.key !== segKey) : prev));
  }

  // Override check: is any assigned talent unavailable on the chosen date?
  // available=null means "not checked yet" — no override warning until the
  // availability fetch lands.
  function isOverride(seg, t) {
    if (!seg?.available || !t.talentId) return false;
    return !seg.available.find(a => a.talentId === t.talentId);
  }
  const hasAnyOverride = segments.some(s => s.talents.some(t => isOverride(s, t)));
  // D-177: the slots the save insists on, and which of them are still open
  const needsCasting = useMemo(() => {
    const map = new Map();
    for (const c of [...(plan?.needsCasting || []), ...serverNeeds]) if (c && c.characterId && !map.has(c.characterId)) map.set(c.characterId, c);
    return [...map.values()];
  }, [plan, serverNeeds]);
  const needIds = new Set(needsCasting.map(c => c.characterId));
  const rowFilled = t => Boolean(t.talentId || t.talentName);
  const pickedFor = cid => segments.some(sg => sg.talents.some(t => t.characterId === cid && rowFilled(t)));
  const unresolvedNeeds = needsCasting.filter(c => !pickedFor(c.characterId) && !uncastIds.has(c.characterId));
  const segHasTalent = sg => (cardMode ? sg.talents.some(rowFilled) : sg.talents.some(t => t.characterId && rowFilled(t)));
  // a segment whose only empty rows are needs-casting characters is not "missing talent" yet — the dialog resolves it
  const segAwaitsDialog = sg => !segHasTalent(sg) && sg.talents.some(t => t.characterId && needIds.has(t.characterId) && !rowFilled(t));
  const missingDay     = segments.some(s => !s.shootDay);
  // 2026-09-03 (owner call): assigning IS the point — every CAM segment must
  // carry at least one character with talent before the plan can be confirmed
  // (talent-less segments polluted the Assigned tab and matched nothing).
  const missingTalent  = segments.some(s => !segHasTalent(s) && !segAwaitsDialog(s));   // card: talent is what matters; Ready: a character with talent
  // No characters to pick from (Riff card, or a script format without character
  // lines): rows are talent-only.
  const talentOnly = allCharacters.length === 0;

  // D-177: dialog actions — pick a performer for a character (the normal talent options, inline), or leave it uncast by choice
  function pickForCharacter(cid, tid) {
    const opt = allTalent.find(t => t.id === tid);
    setUncastIds(prev => { const n = new Set(prev); n.delete(cid); return n; });
    setSegments(prev => {
      const has = prev.some(sg => sg.talents.some(t => t.characterId === cid));
      if (has) return prev.map(sg => ({ ...sg, talents: sg.talents.map(t => t.characterId === cid ? { ...t, talentId: tid || null, talentName: opt?.name || null } : t) }));
      const c = needsCasting.find(x => x.characterId === cid);
      return prev.map((sg, i) => i === 0 ? { ...sg, talents: [...sg.talents.filter(t => t.characterId || rowFilled(t)), { rowKey: nextKey(), characterId: cid, characterName: c?.characterName || null, gender: 'either', talentId: tid || null, talentName: opt?.name || null }] } : sg);
    });
  }
  function leaveUncast(cid) {
    setUncastIds(prev => new Set(prev).add(cid));
    setSegments(prev => prev.map(sg => ({ ...sg, talents: sg.talents.map(t => t.characterId === cid ? { ...t, talentId: null, talentName: null } : t) })));
  }

  async function handleConfirm() {
    // D-177 (G-2): a save with any empty no-default slot is BLOCKED until each one is picked or left uncast
    if (unresolvedNeeds.length) { setCastDialog(unresolvedNeeds.map(c => ({ ...c }))); return; }
    setCastDialog(null);
    const uncastCharacterIds = [...uncastIds].filter(id => needIds.has(id) && !pickedFor(id));
    const saveSegs = segments.filter(segHasTalent);                           // a segment whose characters were all left uncast is not saved
    if (!saveSegs.length) { setErr('Nothing to plan — every character on this plan was left uncast.'); return; }
    setConfirming(true);
    setErr('');
    const onRefusal = e => {                                                    // the server enforces the same rule (an old tab, a stale payload)
      const d = e.response?.data;
      if (d?.code === 'casting_incomplete' && Array.isArray(d.characters)) {
        const named = d.characters.map(c => ({ characterId: c.id, characterName: c.characterName }));
        setServerNeeds(prev => [...prev, ...named]); setCastDialog(named); return true;
      }
      return false;
    };
    if (cardMode) {
      // Production page: the plan REPLACES the card's unfilmed segments
      // (filmed ones are kept server-side).
      const payload = {
        uncastCharacterIds,
        camGroups: saveSegs.map(s => ({
          camAssignment: Number(s.camNumber),
          segmentLabel:  s.segmentLabel,
          shootDay:      s.shootDay || null,
          setting:       s.setting || null,
          talents:       s.talents
            .filter(t => t.talentId || t.talentName)
            .map(t => ({
              characterId:       t.characterId || null,
              talentId:          t.talentId || null,
              talentDisplayName: t.talentId ? null : (t.talentName || null),
            })),
        })),
      };
      try {
        await api.put(`/api/shoot-segments/${card.id}/plan`, payload);
        onConfirmed();
      } catch (e) {
        if (!onRefusal(e)) setErr(e.response?.data?.error || 'Saving the shoot plan failed.');
      } finally { setConfirming(false); }
      return;
    }
    // An Approved script is SUPPOSED to have a Video Card, but a legacy or
    // edge-case script may not — mint one on the spot (idempotent endpoint)
    // instead of erroring the producer out of the flow.
    let lpId = plan?.liveProduction?.id || null;
    if (!lpId) {
      try {
        const r = await api.post(`/api/tal/${scriptId}/ensure-live-production`);
        lpId = r.data.id;
      } catch (e) {
        setErr(e.response?.data?.error || 'Could not create the production for this script.');
        setConfirming(false);
        return;
      }
    }
    const payload = {
      liveProductionId: lpId,
      uncastCharacterIds,
      camGroups: saveSegs.map(s => ({
        camAssignment: Number(s.camNumber),
        segmentLabel:  s.segmentLabel,
        shootDay:      s.shootDay || null,
        setting:       s.setting || null,
        talents:       s.talents
          .filter(t => t.characterId && (t.talentId || t.talentName))
          .map(t => ({
            characterId:       t.characterId,
            talentId:          t.talentId || null,
            talentDisplayName: t.talentId ? null : (t.talentName || null),
          })),
      })),
    };
    try {
      await api.post(`/api/tal/${scriptId}/confirm`, payload);
      onConfirmed();
    } catch (e) {
      if (!onRefusal(e)) setErr(e.response?.data?.error || 'Confirm failed.');
    } finally { setConfirming(false); }
  }

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="vc-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>Loading suggested plan…</div>
        </div>
      </div>
    );
  }
  if (!plan) return null;

  const pickerSeg = calPicker ? segments.find(s => s.key === calPicker) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 820, display: 'flex', flexDirection: 'column' }}>
        <div className="vc-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">{cardMode ? (isEdit ? 'Edit Shoot Plan' : 'Shoot Plan') : 'Schedule Plan'}</div>
            <div className="vc-title">{plan.scriptName}</div>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--tx-muted)' }}>{plan.client?.name}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '16px 28px 0', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {err && (
            <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)', marginBottom: 10, padding: 10, background: 'rgba(212,85,85,0.06)', borderRadius: 6 }}>
              {err}
            </div>
          )}
          {!cardMode && plan.camGroups.length === 0 && (
            <div style={{ padding: 20, color: 'var(--tx-faint)', fontSize: 12 }}>
              This script has no characters in its body yet — add character lines in the
              script editor first. (Untagged characters are fine: they plan as Cam 1.)
            </div>
          )}
          {cardMode && talentOnly && (
            <div style={{ padding: '4px 0 12px', color: 'var(--tx-faint)', fontSize: 11 }} data-testid="plan-talent-only">
              {scriptId ? 'This script has no character lines — assign talent to each CAM segment directly.' : 'Riff production — assign talent to each CAM segment directly.'}
            </div>
          )}
          {cardMode && filmedSegs.length > 0 && (
            <div style={{ marginBottom: 14 }} data-testid="plan-filmed-kept">
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--tx-muted)', marginBottom: 6 }}>
                Filmed — kept as is
              </div>
              {filmedSegs.map(sg => (
                <div key={sg.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, padding: '6px 10px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 6, marginBottom: 4, opacity: 0.8 }}>
                  <span style={{ color: '#4ade80' }}>✓</span>
                  <b>{sg.segmentLabel}</b>
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--tx-muted)' }}>{sg.shootDay ? String(sg.shootDay).slice(0, 10) : 'no day'}</span>
                  <span style={{ color: 'var(--tx-secondary)' }}>{(sg.talents || []).map(t => t.talent?.name || t.talentDisplayName).filter(Boolean).join(', ') || 'no talent'}</span>
                </div>
              ))}
            </div>
          )}
          {segments.map(seg => (
            <div
              key={seg.key}
              style={{
                marginBottom: 18, padding: 14,
                background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                {/* Title is "CAM - N" only — how many characters this segment
                    carries is decided HERE, during scheduling. */}
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--violet)' }}>
                  CAM - {seg.camNumber}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {seg.noCoverage && (
                    <span style={{ fontSize: 10, color: 'var(--s-incomplete-t)' }}>
                      ⚠ No matching talent available in next 30 days
                    </span>
                  )}
                  {segments.length > 1 && (
                    <button
                      className="vc-close"
                      title="Remove this CAM segment from the plan"
                      style={{ fontSize: 13 }}
                      onClick={() => removeSegment(seg.key)}
                    >×</button>
                  )}
                </div>
              </div>

              {/* Row 1: shoot day + segment label + setting */}
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label className="form-label" style={{ fontSize: 10 }}>Shoot day</label>
                  <input
                    type="date"
                    className="form-input"
                    value={seg.shootDay || ''}
                    onChange={e => handleDateChange(seg.key, e.target.value)}
                    onDoubleClick={() => setCalPicker(seg.key)}
                    title="Double-click to pick the day from a full calendar"
                    style={{ fontSize: 12 }}
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: 10 }}>Segment label</label>
                  <input
                    className="form-input"
                    value={seg.segmentLabel || ''}
                    onChange={e => patchSegment(seg.key, { segmentLabel: e.target.value })}
                    style={{ fontSize: 12 }}
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: 10 }}>Setting</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Studio A, kitchen set, etc."
                    value={seg.setting || ''}
                    onChange={e => patchSegment(seg.key, { setting: e.target.value })}
                    style={{ fontSize: 12 }}
                  />
                </div>
              </div>

              {/* Character rows: pick a character from the dropdown; picking
                  one reveals its available-talent dropdown (a CAM segment may
                  not include every character on the script). */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {seg.talents.map(t => {
                  const usedElsewhere = new Set(seg.talents.filter(x => x.rowKey !== t.rowKey).map(x => x.characterId).filter(Boolean));
                  const charOpts = allCharacters.filter(c => !usedElsewhere.has(c.id));
                  // Card mode: talent can be picked without a character (an
                  // existing segment talent may not map to one); Ready mode
                  // keeps character-first.
                  const showTalent = Boolean(t.characterId) || cardMode || talentOnly;
                  const opts     = showTalent ? matchOptions(seg, t.characterId ? t.gender : 'either') : [];
                  const override = isOverride(seg, t);
                  return (
                    <div key={t.rowKey}>
                      <div style={{ display: 'grid', gridTemplateColumns: talentOnly ? '1fr 1fr auto' : '1fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                        {!talentOnly && (
                          <select
                            className="form-input"
                            style={{ fontSize: 11, padding: '4px 8px' }}
                            value={t.characterId || ''}
                            onChange={e => setRowCharacter(seg.key, t.rowKey, e.target.value || null)}
                            title="Which character shoots in this CAM segment"
                          >
                            <option value="">{cardMode ? '— No character —' : '— Pick a character —'}</option>
                            {charOpts.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.characterName} ({c.gender})
                              </option>
                            ))}
                          </select>
                        )}
                        {showTalent ? (
                          <select
                            value={t.talentId || ''}
                            onChange={e => {
                              const tid = e.target.value || null;
                              const opt = opts.find(o => o.talentId === tid);
                              patchRow(seg.key, t.rowKey, { talentId: tid, talentName: opt?.talentName || null });
                            }}
                            className="form-input"
                            style={{ fontSize: 11, padding: '4px 8px' }}
                            title="Available talent for this character"
                          >
                            <option value="">— Unassigned —</option>
                            {opts.map(o => (
                              <option key={o.talentId} value={o.talentId}>
                                {o.talentName} ({o.talentGender}){o.isAvailable ? '' : ' — not available'}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ fontSize: 10, color: 'var(--tx-faint)' }}>Pick a character to choose talent</div>
                        )}
                        <div style={{ fontSize: 10, color: override ? 'var(--s-incomplete-t)' : 'var(--tx-faint)' }}>
                          {override ? `⚠ ${t.talentName} not available on this date — pick a green day below to move the shoot` : ''}
                        </div>
                        <button
                          className="btn-ghost"
                          style={{ fontSize: 10, padding: '2px 7px' }}
                          title="Remove this character row from the segment"
                          onClick={() => removeCharacterRow(seg.key, t.rowKey)}
                        >×</button>
                      </div>
                      {override && t.talentId && (
                        <TalentAvailabilityMini
                          talentId={t.talentId}
                          talentName={t.talentName}
                          baseDate={seg.shootDay}
                          onPick={d => handleDateChange(seg.key, d)}
                        />
                      )}
                    </div>
                  );
                })}
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '4px 10px', alignSelf: 'flex-start' }}
                  onClick={() => addCharacterRow(seg.key)}
                >
                  + Add character
                </button>
              </div>
            </div>
          ))}

          {(cardMode || plan.camGroups.length > 0) && (
            <button
              className="btn-ghost"
              style={{ fontSize: 12, padding: '6px 14px', marginBottom: 16 }}
              title="Add another CAM segment with the same fields — CAM number, shoot day, segment label, setting, characters"
              onClick={addSegment}
            >
              + Add CAM segment
            </button>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 28px', borderTop: '1px solid var(--border)', background: 'var(--bg-card)',
        }}>
          <div style={{ fontSize: 11, color: hasAnyOverride ? 'rgba(255,200,80,0.95)' : 'var(--tx-faint)' }}>
            {(cardMode || plan.camGroups.length > 0) && missingTalent
              ? (cardMode ? 'Every CAM segment needs at least one talent before the plan can be saved.' : 'Every CAM segment needs at least one character with talent assigned before the plan can be confirmed.')
              : hasAnyOverride
                ? '⚠ One or more assignments override availability — proceed only if intentional.'
                : plan.camGroups.length > 0 && missingDay
                  ? 'Shoot days are optional here — book talent on the Talent Calendar, then Schedule from the day’s Shootable Scripts.'
                  : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 14px' }} onClick={onClose}>Cancel</button>
            <button
              className={hasAnyOverride ? 'btn-warn' : 'btn-primary'}
              style={{
                fontSize: 12, padding: '6px 16px',
                background: hasAnyOverride ? 'rgba(255,200,80,0.9)' : undefined,
                color:      hasAnyOverride ? '#1a1a1a' : undefined,
              }}
              onClick={handleConfirm}
              disabled={confirming || segments.length === 0 || missingTalent}
              data-testid="plan-confirm"
            >
              {confirming ? (cardMode ? 'Saving…' : 'Confirming…') : (hasAnyOverride ? (cardMode ? 'Save with Override' : 'Confirm with Override') : (cardMode ? 'Save Plan' : 'Confirm Plan'))}
            </button>
          </div>
        </div>
      </div>

      {/* D-177 (G-2): "Casting incomplete" — every named character must be picked or left uncast before the plan saves */}
      {castDialog && (
        <div className="modal-overlay" data-testid="casting-dialog" onClick={e => { e.stopPropagation(); setCastDialog(null); }} style={{ zIndex: 1200 }}>
          <div className="vc-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, padding: '20px 24px' }}>
            <div data-testid="casting-dialog-title" style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--tx-primary)' }}>Casting incomplete — these characters have no performer:</div>
            <div style={{ display: 'grid', gap: 10 }}>
              {castDialog.map(c => {
                const picked = segments.flatMap(sg => sg.talents).find(t => t.characterId === c.characterId && rowFilled(t));
                const skipped = uncastIds.has(c.characterId) && !picked;
                return (
                  <div key={c.characterId} data-testid="casting-dialog-row" data-character={c.characterName} data-state={picked ? 'picked' : skipped ? 'uncast' : 'open'} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{c.characterName}</span>
                    <select data-testid="casting-dialog-pick" className="form-input" style={{ fontSize: 11, padding: '4px 8px' }} value={picked?.talentId || ''} onChange={e => pickForCharacter(c.characterId, e.target.value || null)}>
                      <option value="">— Pick a performer —</option>
                      {[...allTalent].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(t => <option key={t.id} value={t.id}>{t.name}{t.talentGender ? ` (${t.talentGender})` : ''}</option>)}
                    </select>
                    <button type="button" data-testid="casting-dialog-uncast" className={skipped ? 'btn-primary' : 'btn-ghost'} style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }} onClick={() => leaveUncast(c.characterId)}>No performer — leave uncast</button>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => setCastDialog(null)}>Back to the plan</button>
              <button type="button" data-testid="casting-dialog-save" className="btn-primary" style={{ fontSize: 12, padding: '6px 16px' }} disabled={confirming || castDialog.some(c => !pickedFor(c.characterId) && !uncastIds.has(c.characterId))} onClick={handleConfirm}>{cardMode ? 'Save Plan' : 'Confirm Plan'}</button>
            </div>
          </div>
        </div>
      )}

      {pickerSeg && (
        <BigCalendarPickerModal
          value={pickerSeg.shootDay}
          title={`Shoot day — CAM - ${pickerSeg.camNumber}`}
          onPick={d => handleDateChange(pickerSeg.key, d)}
          onClose={() => setCalPicker(null)}
        />
      )}
    </div>
  );
}
